// Freestyle exercises: when the gym doesn't have the catalogued machine, Marko
// types a name in the picker and logs it like any other exercise. It is saved to
// the local catalog (so it shows up in search next time and builds its own
// history), with plain straight-set defaults. Only name, muscle and dumbbell-or-not
// are asked for; everything else can be tuned later in the seed if it sticks.

import type { Exercise, MuscleGroup } from './types';

export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'exercise';
}

export function buildCustomExercise(
  name: string,
  muscleGroup: MuscleGroup,
  perHand: boolean,
  existingIds: Set<string>,
): Exercise {
  const clean = name.trim().replace(/\s+/g, ' ');
  const base = `custom-${slugify(clean)}`;
  let id = base;
  for (let n = 2; existingIds.has(id); n++) id = `${base}-${n}`;
  return {
    id,
    name: clean,
    muscleGroup,
    structure: 'straight_sets',
    defaultRepRange: { bottom: 8, top: 12 },
    // TOTAL kg: a 2.5 kg/hand dumbbell step is 5 total.
    increment: perHand ? 5 : 2.5,
    equipmentArea: 'either',
    weightEntry: perHand ? 'per_side' : 'total',
    bodyweight: false,
    tracksElbowFeel: false,
    tracksForearmFeel: false,
    tracksGrip: false,
    neverToFailure: false,
    notes: 'Freestyle exercise added from a session.',
    archived: false,
  };
}

// Offer "add as new" only when the typed name isn't already an exercise.
export function shouldOfferCustom(query: string, names: string[]): boolean {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return false;
  return !names.some((n) => n.trim().toLowerCase() === q);
}
